import { useEffect, useState } from "react";
import { ArrowLeft, Plus, Receipt } from "lucide-react";
import { toast } from "sonner";
import { api, money } from "../api";
import { useAuth } from "../auth";
import QuickPaymentModal from "../components/QuickPaymentModal";
import { PriceHistory } from "../components/PriceHistory";

const StatusBadge = ({ s }) => <em className={`status ${s === "Bekliyor" ? "status-amber" : s === "Planlanan" ? "status-blue" : "status-green"}`}>{s}</em>;

export default function SubcontractorDetail({ id, onBack }) {
  const { can } = useAuth();
  const [data, setData] = useState(null);
  const [quick, setQuick] = useState(null);

  const load = () => api.get(`/subcontractors/${id}/detail`).then((r) => setData(r.data)).catch(() => toast.error("Alt yüklenici detayı yüklenemedi"));
  useEffect(() => { load(); }, [id]);

  if (!data) return <main className="content"><p>Yükleniyor…</p></main>;
  const t = data.totals;

  return (
    <main className="content" data-testid="page-sub-detail">
      <button className="link-back" onClick={onBack} data-testid="sub-detail-back"><ArrowLeft size={14} /> Alt yüklenicilere dön</button>
      <div className="page-head">
        <div>
          <span className="section-kicker">ALT YÜKLENİCİ</span>
          <h1>{data.short_name}</h1>
          <p>{data.name}{data.specialty ? ` · ${data.specialty}` : ""}{data.tax_no ? ` · Vergi: ${data.tax_office || "—"} ${data.tax_no}` : ""}{data.phone ? ` · ${data.phone}` : ""}</p>
        </div>
        {can("admin", "project_manager", "finance") && (
          <button className="new-btn" onClick={() => setQuick({ subcontractor_short: data.short_name, direction: "outgoing" })} data-testid="sub-detail-pay-button"><Plus size={15} /> Ödeme Kaydet</button>
        )}
      </div>

      <div className="stats-grid">
        <div className="stat"><div><div className="eyebrow">Toplam Sözleşme</div><strong>{money(t.contract_total)}</strong><span className="stat-detail">{t.job_count} iş kalemi</span></div></div>
        <div className="stat"><div><div className="eyebrow">Ödenen</div><strong>{money(t.paid)}</strong><span className="stat-detail">{t.payment_count} ödeme</span></div></div>
        <div className="stat"><div><div className="eyebrow">Kalan Bakiye</div><strong className="margin">{money(t.remaining)}</strong><span className="stat-detail">Ödenecek</span></div></div>
        <div className="stat"><div><div className="eyebrow">Proje Sayısı</div><strong>{t.project_count}</strong><span className="stat-detail">Aktif iş ilişkisi</span></div></div>
      </div>

      {(data.totals_by_currency || []).length > 1 && (
        <section className="panel" data-testid="sub-currency-totals">
          <div className="panel-head"><div><span className="eyebrow">PARA BİRİMİ BAZINDA</span><h2>Döviz cinsine göre toplamlar</h2></div></div>
          <div className="project-table">
            <div className="table-row table-head cur-tot-row"><span>PARA BİRİMİ</span><span>PROJE</span><span>SÖZLEŞME</span><span>ÖDENEN</span><span>KALAN</span></div>
            {data.totals_by_currency.map((c) => (
              <div className="table-row cur-tot-row" key={c.currency} data-testid={`sub-cur-${c.currency}`}>
                <b>{c.currency}</b>
                <span>{c.project_count}</span>
                <b className="mono-small">{money(c.contract_total, c.currency)}</b>
                <span className="mono-small">{money(c.paid, c.currency)}</span>
                <b className="margin">{money(c.remaining, c.currency)}</b>
              </div>
            ))}
          </div>
        </section>
      )}

      {data.projects.map((g) => (
        <section className="panel" key={g.project_id || g.project_name} data-testid={`sub-detail-project-${g.project_name}`}>
          <div className="panel-head">
            <div><span className="eyebrow">PROJE</span><h2>{g.project_name}</h2></div>
            <b className="mono-small">Sözleşme {money(g.agreed, g.currency)} · Ödenen {money(g.paid, g.currency)} · <span className="margin">Kalan {money(g.remaining, g.currency)}</span></b>
          </div>

          <div className="project-table">
            <div className="table-row table-head wi-row"><span>KALEM</span><span>DURUM</span><span></span><span>ANLAŞILAN</span><span>ÖDENEN</span><span>KALAN</span><span></span></div>
            {g.work_items.map((w) => (
              <div className="wi-block" key={w.id}>
                <div className="table-row wi-row">
                  <b className="wi-cat">{w.category}</b>
                  <span>{w.status || "—"}</span>
                  <span />
                  <b className="mono-small">{money(w.agreed_amount, g.currency)}</b>
                  <span className="mono-small">{money(w.paid_amount, g.currency)}</span>
                  <b className="margin">{money(w.remaining, g.currency)}</b>
                  <span />
                </div>
                {w.price_history?.length > 0 && (
                  <div className="wi-detail"><PriceHistory history={w.price_history} currency={g.currency} testid={`sub-wi-price-history-${w.id}`} /></div>
                )}
              </div>
            ))}
            {!g.work_items.length && <div className="empty-hint small">Bu projede tanımlı iş kalemi yok, yalnızca ödeme kayıtları var.</div>}
          </div>

          {g.payments.length > 0 && (
            <>
              <div className="wi-detail-head" style={{ marginTop: 16 }}>
                <span className="eyebrow"><Receipt size={12} style={{ verticalAlign: "-2px", marginRight: 4 }} />ÖDEME DÖKÜMÜ ({g.payments.length})</span>
              </div>
              {g.payments.map((p) => (
                <div className="mini-pay" key={p.id} data-testid={`sub-payment-${p.id}`}>
                  <span>{p.stage}</span>
                  <span>{p.date ? p.date.slice(0, 10) : "—"}</span>
                  <span>{p.due_date ? `vade ${p.due_date.slice(0, 10)}` : ""}</span>
                  <b className="mono-small">{money(p.amount, p.currency)}</b>
                  <StatusBadge s={p.status} />
                  <small>{p.work_item_category || p.description || ""}</small>
                </div>
              ))}
            </>
          )}
        </section>
      ))}
      {!data.projects.length && <div className="empty-hint">Bu alt yükleniciye bağlı iş veya ödeme kaydı yok.</div>}

      {quick && <QuickPaymentModal preset={quick} close={() => setQuick(null)} onSaved={load} />}
    </main>
  );
}
